import type { CoordinateSystem, GridRequest } from "@mm/api.meteomatics.com";
import { DEFAULT_BINARY_ANOMALY_DEFINITION, getBinaryAnomalyDefinitionById } from "./BinaryAnomalyDefinitions";

export const BINARY_ANOMALY_GRID_SIZE = 64;
export const BINARY_ANOMALY_NO_DATA = -999;
export const BINARY_ANOMALY_INTERPOLATION_MODE = 0 as const;

export type BinaryDifferenceOutputContract = {
  width: typeof BINARY_ANOMALY_GRID_SIZE;
  height: typeof BINARY_ANOMALY_GRID_SIZE;
  noData: typeof BINARY_ANOMALY_NO_DATA;
  interpolationMode: typeof BINARY_ANOMALY_INTERPOLATION_MODE;
};

export const BINARY_ANOMALY_OUTPUT_CONTRACT: BinaryDifferenceOutputContract = Object.freeze({
  width: BINARY_ANOMALY_GRID_SIZE,
  height: BINARY_ANOMALY_GRID_SIZE,
  noData: BINARY_ANOMALY_NO_DATA,
  interpolationMode: BINARY_ANOMALY_INTERPOLATION_MODE,
});

export type BinaryDifferenceStage = "primary" | "reference" | "composition";

export type BinaryDifferenceFailure = {
  binaryDifferenceStage: BinaryDifferenceStage;
  cause: unknown;
};

function optionalAreasMatch(
  primary: GridRequest<CoordinateSystem.EPSG3857>["boundingBoxLimit"],
  reference: GridRequest<CoordinateSystem.EPSG3857>["boundingBoxLimit"],
): boolean {
  if (primary == null || reference == null) {
    return primary === reference;
  }
  return primary.crs === reference.crs && primary.equals(reference);
}

export function assertBinaryDifferenceOutputContract(
  definitionId: string,
  output: BinaryDifferenceOutputContract,
): void {
  if (!getBinaryAnomalyDefinitionById(definitionId)) {
    throw new Error(`unsupported binary anomaly definition: ${definitionId}`);
  }
  if (
    output.width !== BINARY_ANOMALY_OUTPUT_CONTRACT.width ||
    output.height !== BINARY_ANOMALY_OUTPUT_CONTRACT.height ||
    output.noData !== BINARY_ANOMALY_OUTPUT_CONTRACT.noData ||
    output.interpolationMode !== BINARY_ANOMALY_OUTPUT_CONTRACT.interpolationMode
  ) {
    throw new Error("binary anomaly output contract does not match the fixed definition");
  }
}

export function assertMatchingBinaryDifferenceRequests(
  primary: GridRequest<CoordinateSystem.EPSG3857>,
  reference: GridRequest<CoordinateSystem.EPSG3857>,
  definitionId: string = DEFAULT_BINARY_ANOMALY_DEFINITION.id,
): void {
  const definition = getBinaryAnomalyDefinitionById(definitionId);
  if (!definition) {
    throw new Error(`unsupported binary anomaly definition: ${definitionId}`);
  }
  if (primary.width !== BINARY_ANOMALY_GRID_SIZE || primary.height !== BINARY_ANOMALY_GRID_SIZE) {
    throw new Error(`primary binary anomaly request must be ${BINARY_ANOMALY_GRID_SIZE}x${BINARY_ANOMALY_GRID_SIZE}`);
  }
  if (reference.width !== BINARY_ANOMALY_GRID_SIZE || reference.height !== BINARY_ANOMALY_GRID_SIZE) {
    throw new Error(`reference binary anomaly request must be ${BINARY_ANOMALY_GRID_SIZE}x${BINARY_ANOMALY_GRID_SIZE}`);
  }
  if (primary.area.crs !== reference.area.crs || !primary.area.equals(reference.area)) {
    throw new Error("binary anomaly requests must cover the same area in the same CRS");
  }
  if (primary.datetime.toMillis() !== reference.datetime.toMillis()) {
    throw new Error("binary anomaly requests must use the same timestamp");
  }
  if (primary.model !== reference.model) {
    throw new Error("binary anomaly requests must use the same model");
  }
  if (
    primary.parameters.length !== 1 ||
    primary.parameters[0] !== definition.primaryParameter ||
    reference.parameters.length !== 1 ||
    reference.parameters[0] !== definition.referenceParameter
  ) {
    throw new Error(`binary anomaly requests must match definition ${definition.id}`);
  }
  if (JSON.stringify(primary.sampling) !== JSON.stringify(reference.sampling)) {
    throw new Error("binary anomaly requests must use the same sampling strategy");
  }
  if ((primary.apiUrlSuffix ?? "") !== (reference.apiUrlSuffix ?? "")) {
    throw new Error("binary anomaly requests must use the same API URL suffix");
  }
  if (
    primary.calibrated !== reference.calibrated ||
    primary.vertical_interpolation !== reference.vertical_interpolation ||
    primary.decluttered !== reference.decluttered ||
    !optionalAreasMatch(primary.boundingBoxLimit, reference.boundingBoxLimit)
  ) {
    throw new Error("binary anomaly requests must use the same normalization options");
  }
  if (reference.ensSelect != null || reference.initDate != null) {
    throw new Error("binary anomaly reference request must not use ensemble selection or initialization time");
  }
}
